import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import UseCaseFormRenderer from '../simulationUseCase/UseCaseFormRenderer';
import type { UseCaseType } from '../landing/LandingScreen';
import MarketplaceShell from './MarketplaceShell';
import StepIndicator from './StepIndicator';
import { BackLink } from './marketplaceUi';
import { createStudy, saveStudyInputs, submitStudy, type StudySelection } from './marketplaceApi';
import './marketplaceForm.css';

interface FlowState {
  useCase: string;
  selection?: StudySelection;
  selections?: StudySelection[];
  studyId?: number;
  paid?: boolean;
  /** The form's editable state, stashed when advancing to review so a Back
   *  restores the form (and reuses the same draft) instead of starting over. */
  savedFormData?: Record<string, unknown>;
}

// Marketplace use case -> the platform's real use-case form (same components the
// main app uses, with real-time question parsing + image upload/parsing).
const USE_CASE_TO_FORM: Record<string, UseCaseType> = {
  survey: 'survey-and-focus-groups',
  concept: 'vitamin-concept-test',   // new concept UI (result mocked server-side for now)
  packaging: 'packaging-review-v2',
};


const FillStudyScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state || {}) as FlowState;
  const useCase = state.useCase;

  const selections = useMemo<StudySelection[]>(
    () => state.selections || (state.selection ? [state.selection] : []),
    [state],
  );

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chatStarted = useRef(false);
  // Latest editable form state, captured so advancing to review can stash it.
  const lastFormData = useRef<Record<string, unknown>>(state.savedFormData || {});

  // The platform form for this use case ('chat' has no form).
  const formType = useCase ? USE_CASE_TO_FORM[useCase] : undefined;

  const missingContext = !useCase || selections.length === 0;
  const invalidUseCase = !missingContext && useCase !== 'chat' && !formType;
  useEffect(() => {
    if (missingContext || invalidUseCase) navigate('/marketplace/run', { replace: true });
  }, [missingContext, invalidUseCase, navigate]);

  // Chat is interactive (no fill form): create the study and go straight into
  // the live chat (free), or to Stripe first (paid). Runs once on mount.
  useEffect(() => {
    if (missingContext || useCase !== 'chat' || chatStarted.current) return;
    chatStarted.current = true;
    (async () => {
      setBusy(true);
      try {
        const created = await createStudy('chat', selections);
        const res = await submitStudy(created.study_id);
        if (res.checkout_url) { window.location.href = res.checkout_url; return; }
        navigate(`/marketplace/studies/${created.study_id}/chat`, { replace: true });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not start chat');
        setBusy(false);
      }
    })();
  }, [missingContext, useCase, selections, navigate]);

  if (missingContext) return null;

  // Clean full-screen loader while we create + persist the study (a few seconds
  // with a base64 image) — replaces the old translucent overlay band.
  if (busy) {
    return (
      <MarketplaceShell maxWidth="max-w-2xl">
        <div className="flex flex-col items-center py-24 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <h2 className="mt-4 text-[15px] font-medium text-[#111]">Setting up your study…</h2>
          <p className="mt-1 text-[13px] text-[#6b6b6b]">
            One moment while we prepare {state.paid ? 'your review' : 'your run'}.
          </p>
        </div>
      </MarketplaceShell>
    );
  }

  // Create the draft (if needed), persist the filled payload, then route:
  // free -> processing, paid -> the Review page.
  const finalize = async (inputs: Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    try {
      // Reuse an existing draft (e.g. user came Back from review) so we don't
      // create a duplicate draft on every visit.
      let studyId = state.studyId;
      if (!studyId) {
        const created = await createStudy(useCase, selections);
        studyId = created.study_id;
      }
      await saveStudyInputs(studyId, inputs);

      // Persist the draft id + the form's editable state onto THIS history entry,
      // so a Back from review reuses the same draft and restores the form. Then
      // advance to review (free → run, paid → review & pay; it reads study.is_free).
      navigate(location.pathname, {
        replace: true,
        state: { ...state, studyId, savedFormData: lastFormData.current },
      });
      navigate('/marketplace/run/review', { state: { studyId, useCase } });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not submit study');
      setBusy(false);
    }
  };

  // Chat is handled by the effect above (auto-start → live chat); show a loader.
  if (useCase === 'chat') {
    return (
      <MarketplaceShell maxWidth="max-w-2xl">
        <div className="flex flex-col items-center py-24 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <h2 className="mt-4 text-[15px] font-medium text-[#111]">Starting your chat…</h2>
          {error && <div className="mt-3 text-[13px] text-red-600">{error}</div>}
        </div>
      </MarketplaceShell>
    );
  }

  // --- Survey / Concept / Packaging: the platform's real forms ---
  // Invalid useCase is redirected by the effect above; render nothing meanwhile.
  if (!formType) return null;

  // saveMode (onSavePayload) bypasses the enterprise audience/segment
  // requirement — the marketplace resolves Twins server-side. initialFormData +
  // onFormDataChange let the form restore when the user comes Back from review.
  const formEl = (
    <UseCaseFormRenderer
      useCaseType={formType}
      selectedSegmentIds={[]}
      personaFilters={{}}
      marketplaceMode
      initialFormData={state.savedFormData}
      onFormDataChange={(_useCaseType, data) => { lastFormData.current = data; }}
      onBack={() => navigate(-1)}
      onSavePayload={(payload) => { void finalize(payload); }}
    />
  );

  // The concept test is a self-contained, full-screen experience (its own
  // progress + background). Give it the whole canvas rather than boxing it into
  // the narrow step column, where it looked cramped and its background clashed.
  if (useCase === 'concept') {
    return (
      <MarketplaceShell bare>
        {formEl}
        {error && <div className="mx-auto max-w-[720px] px-6 pb-8 text-[13px] text-red-600">{error}</div>}
      </MarketplaceShell>
    );
  }

  // Survey / packaging are card forms — give them a comfortably wide column.
  return (
    <MarketplaceShell maxWidth="max-w-5xl">
      <StepIndicator step={2} labels={['Choose use case', 'Set up study']} />
      <BackLink label="Build audience" onClick={() => navigate(-1)} className="mb-3" />
      <h1 className="text-[22px] font-medium capitalize text-[#111]">{useCase} study</h1>
      <p className="mt-1.5 text-[13px] text-[#6b6b6b]">
        Set up your {useCase} against the selected Twins. {state.paid ? 'You’ll review the cost before paying.' : ''}
      </p>

      {/* Scoped theme remaps the form's enterprise styling to the marketplace
          flat/teal look (no change to the form component or the enterprise app). */}
      <div className="mkt-form-theme mt-6">{formEl}</div>

      {error && <div className="mt-3 text-[13px] text-red-600">{error}</div>}
    </MarketplaceShell>
  );
};

export default FillStudyScreen;
